import React from "react";
import styles from "./EmplyeesListStyles";
import { Body1, Label, Select, Tooltip, useFluent, useScrollbarWidth } from "@fluentui/react-components";
import ReadOnlyInfoBanner from "../common/ReadOnlyInfoBanner";
import {
  Card,
  createTableColumn,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableRowData as RowStateBase,
  useTableFeatures,
} from "@fluentui/react-components/unstable";
import { FixedSizeList as List } from "react-window";
import { AppLoader } from "../common";
import { getSettlementSelectableYears, monthNames } from "../../utils/consts";
import type { IResourceAllocationSummaryListProps, IResourceAllocationSummaryRowApi } from "../../services/types/resourceAllocation";
import ResourceAllocationSummaryRow from "../businessComponenets/ResourceAllocationSummaryRow";

const VIRTUAL_ROW_HEIGHT = 48;

const columns = [
  createTableColumn<IResourceAllocationSummaryRowApi>({ columnId: "userName" }),
  createTableColumn<IResourceAllocationSummaryRowApi>({ columnId: "contractType" }),
  createTableColumn<IResourceAllocationSummaryRowApi>({ columnId: "ordered" }),
  createTableColumn<IResourceAllocationSummaryRowApi>({ columnId: "logged" }),
  createTableColumn<IResourceAllocationSummaryRowApi>({ columnId: "acceptedYouTrack" }),
  createTableColumn<IResourceAllocationSummaryRowApi>({ columnId: "status" }),
  createTableColumn<IResourceAllocationSummaryRowApi>({ columnId: "rejectionReason" }),
  createTableColumn<IResourceAllocationSummaryRowApi>({ columnId: "actions" }),
];

interface TableRowData extends RowStateBase<IResourceAllocationSummaryRowApi> {}

function renderHeaderWithTooltip(label: string, content: string | JSX.Element) {
  return (
    <span style={styles.headerTooltipLabel}>
      <span style={styles.headerTooltipText}>{label}</span>
      <Tooltip relationship="description" content={content}>
        <span style={styles.headerTooltipIcon} aria-label={`Informacja: ${label}`}>i</span>
      </Tooltip>
    </span>
  );
}

function getLoggedMinutesTooltipContent(readOnlyMode: boolean) {
  if (readOnlyMode) {
    return (
      <div>
        Suma czasu zalogowanego w YouTrack w danym miesiącu.
        <br /><br />
        W podglądzie administratora: we wszystkich projektach, w których pracownik pracuje.
      </div>
    );
  }

  return (
    <div>
      Suma czasu zalogowanego w YouTrack w danym miesiącu.
      <br /><br />
      Tylko projekty wspólne — te, którymi zarządzasz i w których pracuje dany pracownik.
      <br /><br />
      Uwzględniane są wszystkie wpisy w tych projektach, nie tylko ze zleceń.
    </div>
  );
}

const ResourceAllocationSummaryList: React.FC<IResourceAllocationSummaryListProps> = ({
  rows,
  isLoading,
  readOnlyMode,
  selectedMonth,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
  filterEmployees,
  selectedEmployeeId,
  setSelectedEmployeeId,
  acceptSettlement,
  rejectSettlement,
  onRowClick,
}) => {
  const { targetDocument } = useFluent();
  const scrollbarWidth = useScrollbarWidth({ targetDocument });

  const { getRows } = useTableFeatures({ columns, items: rows });
  const tableRows: TableRowData[] = getRows((row) => row);

  const onMonthSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(e.target.value);
  };

  const onYearSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(e.target.value, 10));
  };

  const onEmployeeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedEmployeeId(e.target.value);
  };

  return (
    <Card style={styles.tableContainer}>
      <Body1>
        <div style={styles.tableWithFiltersHeader}>
          {readOnlyMode ? (
            <ReadOnlyInfoBanner message="Tryb podglądu — widoczni wszyscy pracownicy organizacji." />
          ) : null}
          <div style={styles.resourceAllocationToolbarRow}>
            <div style={styles.toolbarFilterGroup}>
              <Label size="small" htmlFor="resource-allocation-summary-month">
                Miesiąc
              </Label>
              <Select
                id="resource-allocation-summary-month"
                style={styles.tableFilter}
                onChange={onMonthSelect}
                value={selectedMonth}
              >
                {monthNames.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </Select>
            </div>
            <div style={styles.toolbarFilterGroup}>
              <Label size="small" htmlFor="resource-allocation-summary-year">
                Rok
              </Label>
              <Select
                id="resource-allocation-summary-year"
                style={styles.tableFilterYear}
                onChange={onYearSelect}
                value={selectedYear}
              >
                {getSettlementSelectableYears().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </div>
            <div style={styles.toolbarFilterGroup}>
              <Label size="small" htmlFor="resource-allocation-summary-employee">
                Pracownik
              </Label>
              <Select
                id="resource-allocation-summary-employee"
                style={styles.tableFilter}
                onChange={onEmployeeSelect}
                value={selectedEmployeeId}
              >
                <option value="">Wszyscy</option>
                {filterEmployees.map((employee) => (
                  <option key={employee.employeeId} value={employee.employeeId}>
                    {employee.lastName} {employee.firstName}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        <div className="main-table-container">
          <Table
            noNativeElements
            aria-label="Tabela alokacji zasobów"
            aria-rowcount={tableRows.length}
            style={styles.table}
          >
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Nazwisko i imię</TableHeaderCell>
                <TableHeaderCell>Typ umowy</TableHeaderCell>
                <TableHeaderCell>Zlecone godziny</TableHeaderCell>
                <TableHeaderCell>
                  {renderHeaderWithTooltip(
                    "Zalogowane godziny",
                    getLoggedMinutesTooltipContent(readOnlyMode)
                  )}
                </TableHeaderCell>
                <TableHeaderCell>Zaakceptowane godziny</TableHeaderCell>
                <TableHeaderCell style={styles.statusHeaderCell}>Status rozliczenia</TableHeaderCell>
                <TableHeaderCell style={styles.rejectionReasonHeaderCell}>
                  Powód odrzucenia
                </TableHeaderCell>
                {!readOnlyMode ? (
                  <TableHeaderCell style={styles.actionsHeaderCell}>Akcje</TableHeaderCell>
                ) : null}
                <div role="presentation" style={{ width: scrollbarWidth }} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableRows.length <= 0 && !isLoading && (
                <h4 className="table-empty-value">Brak danych</h4>
              )}

              <List
                height={350}
                itemCount={tableRows.length}
                itemSize={VIRTUAL_ROW_HEIGHT}
                width="100%"
                itemData={tableRows}
              >
                {({ index, style, data }) => (
                  <ResourceAllocationSummaryRow
                    index={index}
                    style={style}
                    data={data}
                    readOnlyMode={readOnlyMode}
                    acceptSettlement={acceptSettlement}
                    rejectSettlement={rejectSettlement}
                    onRowClick={onRowClick}
                  />
                )}
              </List>

              {isLoading && rows.length === 0 && <AppLoader />}
            </TableBody>
          </Table>
        </div>
      </Body1>
    </Card>
  );
};

export default ResourceAllocationSummaryList;
